import { useState, useMemo, useEffect, useCallback } from 'react'
import { useGetClubMembersManagement, useGetClubPendingInvites } from '@/services/club'
import {
  ClubMember,
  ClubMemberView,
  ClubMembersManagementPayload,
  ClubMembersSearchFilter,
  ClubMembersSearchSort,
  EntitlementType,
} from '@/types'

const DEFAULT_PAGE_SIZE = 25

// Display tabs — UNCLAIMED_INVITE is merged into PENDING_INVITE by the ES endpoint
export type MemberDisplayTab = 'ACTIVE' | 'PENDING_JOIN_REQUEST' | 'PENDING_INVITE'

export const DISPLAY_TABS: { id: MemberDisplayTab; label: string }[] = [
  { id: 'ACTIVE', label: 'Active' },
  { id: 'PENDING_JOIN_REQUEST', label: 'Pending Requests' },
  { id: 'PENDING_INVITE', label: 'Pending Invites' },
]

// Both pending tabs are served by the pending-invites endpoint, split by how the row was created
// (clubJoinType): REQUEST when the player asked to join, INVITATION / INVITATION_CSV when a manager
// added them. That is the split the by-state endpoint, the old Clubs tab, the mobile app and the
// approve / decline-requests endpoints all use. The dedicated /pending/requests endpoint does not:
// it returns every pending player whose requester is not a current director or organizer, so
// invitations sent by admins, club staff or since-removed organizers showed up as "requests" that
// could be neither approved nor declined, and were counted under both tabs.
const JOIN_REQUEST_TYPES = ['REQUEST']
const INVITE_TYPES = ['INVITATION', 'INVITATION_CSV']

/**
 * Pins a pending-members filter to one side of the join-type split. The endpoint honours only two
 * shapes of clubInvitationType: exactly ['REQUEST'] lists join requests, and two or more invite
 * types list invitations. A lone invite type is ignored and the list falls back to every pending
 * player, requests included, so the caller's own value is always replaced.
 */
function withJoinTypes(filter: ClubMembersSearchFilter | undefined, types: string[]): ClubMembersSearchFilter {
  return { ...filter, clubInvitationType: types }
}

export type SortColumn = 'name' | 'singles' | 'doubles' | 'joined'
export type SortDir = 'asc' | 'desc' | null

/** Maps frontend sort state to backend sort params */
function toBackendSort(col: SortColumn | null, dir: SortDir): ClubMembersSearchSort | undefined {
  if (!col || !dir) return undefined
  const paramMap: Record<SortColumn, ClubMembersSearchSort['parameter']> = {
    name: 'fullNameSort',
    singles: 'singles',
    doubles: 'doubles',
    joined: 'roles.created',
  }
  return { parameter: paramMap[col], order: dir === 'asc' ? 'ASC' : 'DESC' }
}

/** Converts flat entitlement keys ["op:TYPE", ...] into Record<string, EntitlementType[]> */
function parseEntitlementKeys(keys?: string[]): Record<string, EntitlementType[]> | undefined {
  if (!keys || keys.length === 0) return undefined
  const result: Record<string, EntitlementType[]> = {}
  for (const key of keys) {
    const colonIdx = key.indexOf(':')
    if (colonIdx < 0) continue
    const op = key.substring(0, colonIdx)
    const ent = key.substring(colonIdx + 1) as EntitlementType
    if (!ent) continue
    if (!result[op]) result[op] = []
    result[op].push(ent)
  }
  return Object.keys(result).length > 0 ? result : undefined
}

/** Maps a ClubMember (ES response) to ClubMemberView for the management UI */
function toMemberView(member: ClubMember, state: MemberDisplayTab): ClubMemberView {
  return {
    userId: member.id,
    fullName: member.fullName,
    duprId: member.duprId || '',
    singles: member.singles || 'NR',
    doubles: member.doubles || 'NR',
    state,
    invitationType: member.invitationType || '',
    joinedAt: (member.created || member.roles?.[0]?.created || '') as string,
    entitlements: parseEntitlementKeys(member.entitlementKeys),
  }
}

export interface UseClubMembersParams {
  query?: string
  sortColumn?: SortColumn | null
  sortDir?: SortDir
  filter?: ClubMembersSearchFilter
}

export const useClubMembers = (clubId: number, params: UseClubMembersParams = {}, enabled = true) => {
  const { query, sortColumn = null, sortDir = null, filter } = params

  const [tab, setTab] = useState<MemberDisplayTab>('ACTIVE')
  const [offset, setOffset] = useState(0)

  // Reset offset when search/sort/filter change
  useEffect(() => {
    setOffset(0)
  }, [query, sortColumn, sortDir, JSON.stringify(filter)])

  const backendSort = toBackendSort(sortColumn, sortDir)

  // Build payloads directly — avoids stale closure issues with useCallback/useMemo chains
  const basePayload: ClubMembersManagementPayload = {
    limit: DEFAULT_PAGE_SIZE,
    offset,
    query: query || '*',
    ...(backendSort ? { sort: backendSort } : {}),
  }

  const activePayload: ClubMembersManagementPayload = {
    ...basePayload,
    ...(filter ? { filter } : {}),
    includeStaff: true,
    includePendingPlayers: false,
  }
  const invitePayload: ClubMembersManagementPayload = { ...basePayload, filter: withJoinTypes(filter, INVITE_TYPES) }
  const requestPayload: ClubMembersManagementPayload = {
    ...basePayload,
    filter: withJoinTypes(filter, JOIN_REQUEST_TYPES),
  }

  // Count payloads (no user filters, limit 1) — stable across renders
  const countPayloadActive = useMemo<ClubMembersManagementPayload>(
    () => ({ limit: 1, offset: 0, query: '*', includeStaff: true, includePendingPlayers: false }),
    []
  )
  const countPayloadInvites = useMemo<ClubMembersManagementPayload>(
    () => ({ limit: 1, offset: 0, query: '*', filter: withJoinTypes(undefined, INVITE_TYPES) }),
    []
  )
  const countPayloadRequests = useMemo<ClubMembersManagementPayload>(
    () => ({ limit: 1, offset: 0, query: '*', filter: withJoinTypes(undefined, JOIN_REQUEST_TYPES) }),
    []
  )

  const activeQuery = useGetClubMembersManagement(clubId, activePayload, enabled && tab === 'ACTIVE')
  const inviteQuery = useGetClubPendingInvites(clubId, invitePayload, enabled && tab === 'PENDING_INVITE')
  const requestQuery = useGetClubPendingInvites(clubId, requestPayload, enabled && tab === 'PENDING_JOIN_REQUEST')

  const activeCount = useGetClubMembersManagement(clubId, countPayloadActive, enabled && tab !== 'ACTIVE')
  const inviteCount = useGetClubPendingInvites(clubId, countPayloadInvites, enabled && tab !== 'PENDING_INVITE')
  const requestCount = useGetClubPendingInvites(clubId, countPayloadRequests, enabled && tab !== 'PENDING_JOIN_REQUEST')

  // Select the active query based on tab
  const currentQuery = tab === 'ACTIVE' ? activeQuery : tab === 'PENDING_INVITE' ? inviteQuery : requestQuery

  const rawHits = currentQuery.data?.result?.hits ?? []
  const total = currentQuery.data?.result?.total ?? 0
  const hasMore = currentQuery.data?.result?.hasMore ?? false

  // Map ES response to ClubMemberView
  const members = useMemo(() => rawHits.map((m) => toMemberView(m, tab)), [rawHits, tab])

  // Build tab counts
  const countsByTab = useMemo(() => {
    const counts: Record<string, number | undefined> = {}
    counts[tab] = total
    if (tab !== 'ACTIVE') counts['ACTIVE'] = activeCount.data?.result?.total
    if (tab !== 'PENDING_INVITE') counts['PENDING_INVITE'] = inviteCount.data?.result?.total
    if (tab !== 'PENDING_JOIN_REQUEST') counts['PENDING_JOIN_REQUEST'] = requestCount.data?.result?.total
    return counts
  }, [tab, total, activeCount.data, inviteCount.data, requestCount.data])

  // Tri-state sort toggle (managed by parent via params, but exposed for convenience)
  const toggleSort = useCallback(
    (_col: SortColumn) => {
      // No-op — sorting is driven by parent via params
    },
    []
  )

  const changeTab = useCallback((newTab: MemberDisplayTab) => {
    setTab(newTab)
    setOffset(0)
  }, [])

  const nextPage = useCallback(() => {
    if (hasMore) setOffset((prev) => prev + DEFAULT_PAGE_SIZE)
  }, [hasMore])

  const prevPage = useCallback(() => {
    setOffset((prev) => Math.max(0, prev - DEFAULT_PAGE_SIZE))
  }, [])

  return {
    members,
    total,
    hasMore,
    isLoading: currentQuery.isLoading,
    isFetching: currentQuery.isFetching,
    tab,
    offset,
    pageSize: DEFAULT_PAGE_SIZE,
    countsByTab,
    sortColumn,
    sortDir,
    toggleSort,
    changeTab,
    nextPage,
    prevPage,
  }
}
